import {
  GetMagazineDetailQuery,
  GetMagazinesBannerQuery,
  GetMagazinesCurationQuery,
  GetMagazinesQuery,
  GetRandomMagazinesQuery,
} from './bff/gql-schema';

export type MagazineBanner = GetMagazinesBannerQuery['magazineCursorPage']['elements'][0];

export interface MagazineBannerList {
  elements: Array<MagazineBanner>;
}
type MagazineQueryType = GetMagazinesQuery['magazineCursorPage']['elements'][0];
export interface Magazine extends MagazineQueryType {
  createdDate: string;
}
type MagazineDetailQueryType = GetMagazineDetailQuery['magazineV2'];
export interface MagazineDetail extends MagazineDetailQueryType {
  createdDate: string;
}

type RandomMagazineQueryType = GetRandomMagazinesQuery['randomMagazines'][0];

export interface RandomMagazine extends RandomMagazineQueryType {
  createdDate: string;
}

type MagazineCurationQueryType = GetMagazinesCurationQuery['magazineCursorPage']['elements'][0];

export interface MagazineCuration extends MagazineCurationQueryType {
  createdDate: string;
}

export interface MagazineLineItemVo {
  readonly title: string;
  readonly mobileTitle: string;
  readonly image: string;
  readonly date: string | null;
  readonly magazinePid: string;
  readonly description: string;
  readonly mobileDescription: string;
  readonly linkUrl?: string;
  readonly siteName?: string;
}

export interface HomeMagazineItemVo {
  readonly title: string;
  readonly image: string;
  readonly date: string | null;
  readonly magazinePid: string;
}

export const MagazineType = {
  INTERNAL: 'INTERNAL',
  EXTERNAL: 'EXTERNAL',
  ARTICLE: 'ARTICLE',
  FAQ: 'FAQ',
} as const;

export type MagazineType = (typeof MagazineType)[keyof typeof MagazineType];

export enum MagazineTagType {
  DEPARTMENT = 'DEPARTMENT',
  SYMPTOM = 'SYMPTOM',
  DISEASE = 'DISEASE',
  FREE = 'FREE',
}
export enum CoreCategoryType {
  Department = 'DEPARTMENT',
  Disease = 'DISEASE',
  Symptom = 'SYMPTOM',
}
export interface CoreCategory {
  readonly coreId: string;
  readonly coreType: CoreCategoryType;
}

export interface MagazineTag {
  readonly name: string;
  readonly tagId: string;
  readonly tagType: MagazineTagType;
  readonly coreCategories: Array<CoreCategory>;
}

export interface InternalMagazineTag extends MagazineTag {
  readonly magazinePid: string;
}

export enum AuthorType {
  DOCTOR = 'DOCTOR',
  AI = 'AI',
  NURSE = 'NURSE',
  MEDICAL_PROFESSIONAL_EDITOR = 'MEDICAL_PROFESSIONAL_EDITOR',
  GENERAL_EDITOR = 'GENERAL_EDITOR',
}

enum ProfessionalFieldType {
  DEPARTMENT = 'DEPARTMENT',
}

export interface ProfessionalFields {
  name: string;
  professionalFieldId: string;
  professionalFieldType: ProfessionalFieldType;
}

export enum MagazineBottomButtonType {
  QUESTION = 'QUESTION',
  DIAGNOSIS = 'DIAGNOSIS',
}
export interface LdFaqJson {
  '@context': string;
  '@type': 'QAPage';
  mainEntity: {
    '@type': 'Question';
    name: string;
    text: string;
    dateCreated: string;
    answerCount: number;
    upvoteCount: number;
    author: {
      '@type': 'Organization';
      name: string;
      url: string;
    };
    acceptedAnswer: {
      '@type': 'Answer';
      text: string;
      dateCreated: string;
      upvoteCount: number;
      url: string;
      author: {
        '@type': 'Organization';
        url: string;
        name: string;
      };
    };
  };
}

export enum EDoctorCareer {
  CURRENT = 'CURRENT',
  PAST = 'PAST',
  GRADUATION = 'GRADUATION',
  COMPLETION = 'COMPLETION',
}

export interface MagazineAuthor {
  affiliatedInstitutionName: string;
  authorId: string;
  authorPublicId: string;
  authorType: AuthorType;
  externalLinkUrl: string;
  name: string;
  professionalFields: Array<ProfessionalFields>;
  profileImageUrl: string;
  profileDescription?: string;
  careers: {
    content: string;
    careerType: EDoctorCareer;
  }[];
}

export interface MagazineDetailBanner {
  magazineId: string;
  externalUrl: string;
  title: string;
  description: string;
  imageUrl: string;
  buttonPhrase: string;
}
export interface MagazineDetailVo {
  readonly author: MagazineAuthor;
  readonly content: string;
  readonly createdAt: number;
  readonly description: string;
  readonly footnote: string;
  readonly isLikedByMe: boolean;
  readonly likeCount: number;
  readonly magazinePid: string;
  readonly questionCategoryId: string;
  readonly title: string;
  readonly titleImageUrl: string;
  readonly mobileContent: string;
  readonly mobileDescription: string;
  readonly mobileTitle: string;
  readonly bottomButtonType: MagazineBottomButtonType;
  readonly tags: Array<InternalMagazineTag>;
  readonly tagNames: string[];
  isExternalWriter: boolean;
  hospitalName: string;
  hospitalPid: string;
  hospitalAddress: string;
  hospitalProfileImageUrl?: string;
  readonly banner?: MagazineDetailBanner;
  readonly hasJapanese: boolean;
}

export interface MagazineListItemDto {
  readonly content: string;
  readonly createdAt: number;
  readonly description: string;
  readonly magazinePid: string;
  readonly title: string;
  readonly titleImageUrl: string;
  readonly mobileContent: string;
  readonly mobileDescription: string;
  readonly mobileTitle: string;
}

export interface MagazineVo {
  readonly content: string;
  readonly date: string;
  readonly description: string;
  readonly magazinePid: string;
  readonly title: string;
  readonly titleImageUrl: string;
  readonly mobileContent: string;
  readonly mobileDescription: string;
  readonly mobileTitle: string;
}
export type MagazineTagListVo = MagazineVo;

export interface RelatedMagazinesVo extends MagazineVo {
  readonly siteName?: string;
  readonly isExternal?: boolean;
  readonly contentType: string;
  readonly isArticle: boolean;
}
